import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  loginTime: localStorage.getItem('loginTime') ? new Date(localStorage.getItem('loginTime')) : null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      const loginTime = new Date();
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('loginTime', loginTime.toISOString());
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        loginTime: loginTime,
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('loginTime');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        loginTime: null,
      };
    case 'LOAD_USER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOAD_USER_FAIL':
      // Eğer keepToken true ise, token'ı koru (API hatası durumunda)
      if (action.payload?.keepToken) {
        return {
          ...state,
          user: null,
          isAuthenticated: false,
          loading: false,
        };
      }
      // Normal logout durumu
      localStorage.removeItem('token');
      localStorage.removeItem('loginTime');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        loginTime: null,
      };
    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const sessionTimeoutRef = useRef(null);

  // Oturum süresi (24 saat - mobil için daha uygun)
  const SESSION_DURATION = useMemo(() => 24 * 60 * 60 * 1000, []); // 24 saat

  // Sayfa yüklendiğinde kullanıcıyı kontrol et
  useEffect(() => {
    if (state.token) {
      // Oturum süresini kontrol et
      if (state.loginTime) {
        const now = new Date();
        const timeDiff = now.getTime() - state.loginTime.getTime();
        
        if (timeDiff > SESSION_DURATION) {
          toast.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
          dispatch({ type: 'LOGOUT' });
          return;
        }
        
        // Kalan süreyi hesapla ve timeout ayarla
        const remainingTime = SESSION_DURATION - timeDiff;
        setSessionTimeout(remainingTime);
      }
      
      // Kullanıcı bilgisi yoksa loadUser çağır
      if (!state.user) {
        loadUser();
      }
    } else {
      // Token yoksa loading'i false yap
      dispatch({ type: 'LOAD_USER_FAIL', payload: { keepToken: true } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token, state.loginTime]);

  // Oturum timeout'unu ayarla
  const setSessionTimeout = (duration) => {
    // Önceki timeout'u temizle
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    // Yeni timeout ayarla
    sessionTimeoutRef.current = setTimeout(() => {
      toast.error('Oturum süreniz doldu. Güvenliğiniz için otomatik çıkış yapılıyor.');
      dispatch({ type: 'LOGOUT' });
    }, duration);
  };

  // Oturum timeout'unu temizle
  const clearSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  };

  // Component unmount olduğunda timeout'u temizle
  useEffect(() => {
    return () => {
      clearSessionTimeout();
    };
  }, []);

  // Kullanıcı aktivitesini yenile (oturum süresini uzat)
  const refreshSession = useCallback(() => {
    if (state.isAuthenticated && state.loginTime) {
      const now = new Date();
      const timeDiff = now.getTime() - state.loginTime.getTime();
      
      // Eğer oturum hala geçerliyse, süreyi yenile
      if (timeDiff < SESSION_DURATION) {
        const newLoginTime = new Date();
        localStorage.setItem('loginTime', newLoginTime.toISOString());
        setSessionTimeout(SESSION_DURATION);
        
        // State'i güncelle
        dispatch({
          type: 'UPDATE_PROFILE',
          payload: { loginTime: newLoginTime }
        });
      }
    }
  }, [state.isAuthenticated, state.loginTime, SESSION_DURATION]);

  // Kullanıcı aktivitesini takip et
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleUserActivity = () => {
      refreshSession();
    };

    // Kullanıcı aktivitelerini dinle
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [state.isAuthenticated, refreshSession]);

  const loadUser = async () => {
    try {
      const response = await authAPI.getMe();
      dispatch({
        type: 'LOAD_USER_SUCCESS',
        payload: response.data.user,
      });
    } catch (error) {
      // 401 hatası durumunda token'ı temizle
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('loginTime');
        dispatch({ 
          type: 'LOAD_USER_FAIL',
          payload: { keepToken: false }
        });
        return;
      }
      
      // Diğer hatalarda loading'i false yap
      dispatch({ 
        type: 'LOAD_USER_FAIL',
        payload: { keepToken: true }
      });
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: response.data,
      });
      
      // Oturum timeout'unu ayarla
      setSessionTimeout(SESSION_DURATION);
      
      toast.success('Giriş başarılı!');
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      const message = data?.message || 'Giriş yapılamadı';
      if (error.response?.status === 403 && data?.requiresVerification) {
        toast.error(message);
        return {
          success: false,
          requiresVerification: true,
          email: data.email,
          message
        };
      }
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);

      if (response.data.requiresVerification) {
        toast.success(response.data.message || 'Doğrulama kodu gönderildi');
        return {
          success: true,
          requiresVerification: true,
          email: response.data.email || userData.email
        };
      }

      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: response.data
      });
      setSessionTimeout(SESSION_DURATION);
      toast.success('Kayıt başarılı!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Kayıt yapılamadı';
      toast.error(message);
      return { success: false, message };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await authAPI.verifyEmail(email, code);
      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: response.data
      });
      setSessionTimeout(SESSION_DURATION);
      toast.success('E-posta doğrulandı!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Doğrulama başarısız';
      toast.error(message);
      return { success: false, message };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await authAPI.resendVerification(email);
      toast.success(response.data.message || 'Kod gönderildi');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Kod gönderilemedi';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    clearSessionTimeout();
    dispatch({ type: 'LOGOUT' });
    toast.success('Çıkış yapıldı');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      dispatch({
        type: 'UPDATE_PROFILE',
        payload: response.data.user,
      });
      toast.success('Profil güncellendi!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profil güncellenemedi';
      toast.error(message);
      return { success: false, message };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData);
      toast.success('Şifre değiştirildi!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Şifre değiştirilemedi';
      toast.error(message);
      return { success: false, message };
    }
  };


  const value = {
    ...state,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    updateProfile,
    changePassword,
    loadUser,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


